import React, { useEffect, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import { useDispatch, useSelector } from "react-redux";
import { assignTraineeActions } from "../../Store/AssignTrainee";
import Grid from "@mui/material/Grid";
import { Alert, IconButton, Paper, Popover, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { not, intersection, union } from "./utils";
import FormControlLabel from "@mui/material/FormControlLabel";
import { isEditable } from "./utils";
import { useTranslation } from "react-i18next";
import classes from "../AssignTrainees/CustomList.module.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { green, red } from "@mui/material/colors";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PropTypes from "prop-types";
import SnackBar from "components/SnackBar/SnackBar";
import { SnackBarStoreAction } from "Store/SnackBar";

const CustomList = (props) => {
  const theme = createTheme({
    typography: {
      fontFamily: "Urbanist, sans-serif",
    },

    palette: {
      primary: {
        main: green[500],
      },
      secondary: {
        main: red[500],
      },
      action: {
        disabled: "#636362",
      },
    },
  });
  const {
    items,
    rosterId,
    isRosterLocked,
    canRosterCreateIdmTicket,
    techEquipmentSolutionId,
  } = props;
  const { t } = useTranslation(["AssignTrainees"]);

  const assignTraineeData = useSelector((state) => state.assignTrainees);
  const requisitionID = assignTraineeData?.requisitionID;
  const left = assignTraineeData?.sourceList;
  const right = items;
  const checked = assignTraineeData?.checked;
  const dispatch = useDispatch();

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const [idmAnchorEl, setIdmAnchorEl] = React.useState(null);

  const handleIdmPopoverOpen = (event) => {
    setIdmAnchorEl(event.currentTarget);
  };

  const handleIdmPopoverClose = () => {
    setIdmAnchorEl(null);
  };

  const openIdmPopover = Boolean(idmAnchorEl);

  const traineesSource = items.map((value) => {
    return {
      id: value,
      isEditable: isEditable(value, requisitionID),
    };
  });

  const editable = traineesSource
    .filter((value) => value?.isEditable === true)
    .map((value) => value?.id);
  const nonEditable = traineesSource
    .filter((value) => value?.isEditable === false)
    .map((value) => value?.id);

  useEffect(() => {
    const handleModifiedRosters = () => {
      if (editable.length > 0) {
        dispatch(assignTraineeActions.addModifiedRosters(rosterId));
      }
      if (editable.length === 0) {
        dispatch(assignTraineeActions.removeModifiedRoster(rosterId));
      }
    };

    handleModifiedRosters();
  }, [items]);

  const getTraineeName = (oracleId) => {
    let value = oracleId.split("-").shift();
    const traineesData = assignTraineeData.traineesData;
    const traineeDetails = traineesData.filter(
      (item) => item?.OracleId === value,
    );
    const details = Object.assign({}, ...traineeDetails);
    return `${value} - ${details?.FirstName} ${details?.LastName}`;
  };

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    dispatch(assignTraineeActions.setChecked(newChecked)); //Setting Checked globally
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      dispatch(assignTraineeActions.setChecked(not(checked, items))); //Setting Checked globally
    } else {
      dispatch(assignTraineeActions.setChecked(union(checked, items))); //Setting Checked globally
    }
  };

  const handleIncludeIDM = () => {
    if (assignTraineeData?.IDMRosters?.length > 0) {
      showSnack("Only one Roster can be included for IDM Ticket.", "info");
    }
    dispatch(assignTraineeActions.setIncludeInIDM(rosterId));
  };

  const handleCheckedRight = () => {
    dispatch(
      assignTraineeActions.setRosterList({
        rosterId: rosterId,
        Ids: right.concat(leftChecked),
      }),
    );
    dispatch(assignTraineeActions.setSourceList(not(left, leftChecked)));
    dispatch(assignTraineeActions.setChecked(not(checked, leftChecked)));
  };

  const handleCheckedLeft = () => {
    dispatch(assignTraineeActions.setSourceList(left.concat(rightChecked)));
    dispatch(
      assignTraineeActions.setRosterList({
        rosterId: rosterId,
        Ids: not(right, rightChecked),
      }),
    );
    dispatch(assignTraineeActions.setChecked(not(checked, rightChecked)));
  };

  const idmWarningStyle = {
    fontSize: 12,
    fontFamily: "Urbanist, sans-serif",
  };

  return (
    <Paper
      elevation={2}
      sx={{
        marginTop: "10px",
      }}
    >
      <ThemeProvider theme={theme}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} sx={{ padding: 1 }}>
                <span>
                  <b
                    sx={{
                      fontFamily: "Urbanist, sans-serif",
                    }}
                  >{`${t("Roster")} : ${rosterId}`}</b>
                </span>
              </Grid>
              <Grid item xs={4} sx={{ paddingLeft: 1 }}>
                <Grid container>
                  <Grid item xs={6}>
                    <IconButton
                      color="primary"
                      variant="contained"
                      size="small"
                      onClick={handleCheckedRight}
                      sx={{ borderRadius: 1, backgroundColor: green[100] }}
                      disabled={leftChecked.length === 0 || isRosterLocked}
                      {...(isRosterLocked && {
                        title:
                          "Roster is locked, please unlock the roster to Assign trainees.",
                      })}
                      aria-label="move selected right"
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>

                  <Grid item xs={6}>
                    <IconButton
                      color="secondary"
                      variant="contained"
                      size="small"
                      onClick={handleCheckedLeft}
                      sx={{ borderRadius: 1, backgroundColor: red[100] }}
                      disabled={rightChecked.length === 0 || isRosterLocked}
                      {...(isRosterLocked && {
                        title:
                          "Roster is locked, please unlock the roster to Assign trainees.",
                      })}
                      aria-label="move selected left"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={1}></Grid>
              <Grid item xs={7}>
                {canRosterCreateIdmTicket ? (
                  <FormControlLabel
                    sx={{
                      height: "5px",
                    }}
                    control={
                      <Checkbox
                        sx={{
                          "&.Mui-checked": {
                            color: "#3299FF",
                          },
                        }}
                        disableRipple
                        disabled={items?.length === 0}
                        checked={assignTraineeData?.IDMRosters?.includes(
                          rosterId,
                        )}
                        onChange={handleIncludeIDM}
                      />
                    }
                    label={
                      <Typography sx={idmWarningStyle}>
                        {t("Include in IDM ticket")}
                      </Typography>
                    }
                  />
                ) : (
                  <Grid
                    container
                    onMouseEnter={handleIdmPopoverOpen}
                    onMouseLeave={handleIdmPopoverClose}
                  >
                    <Grid item xs={2}>
                      <ErrorOutlineIcon
                        sx={{ marginRight: "5px", color: "#E23636" }}
                      />
                    </Grid>
                    <Grid item xs={10}>
                      <Typography
                        sx={{
                          ...idmWarningStyle,
                          color: "grey",
                        }}
                      >
                        {t("Include in IDM ticket")}
                      </Typography>
                      <Popover
                        id="mouse-over-popover"
                        sx={{ pointerEvents: "none" }}
                        open={openIdmPopover}
                        anchorPosition={{ top: 800, left: 400 }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        onClose={handleIdmPopoverClose}
                        disableRestoreFocus
                      >
                        <Alert
                          severity="error"
                          sx={{
                            fontFamily: "Urbanist, sans-serif",
                          }}
                        >
                          {techEquipmentSolutionId !== 14
                            ? t("idmWarning")
                            : t("idmTicketWarningTTF")}
                        </Alert>
                      </Popover>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12}>
                <Card
                  sx={{
                    maxHeight: "calc(100vh - 280px)",
                    overflowY: "auto",
                  }}
                >
                  <CardHeader
                    sx={{ px: 2, py: 1 }}
                    className={classes.cardHeader}
                    avatar={
                      <Checkbox
                        onClick={handleToggleAll(editable)}
                        checked={
                          numberOfChecked(editable) === editable.length &&
                          editable.length !== 0
                        }
                        indeterminate={
                          numberOfChecked(editable) !== editable.length &&
                          numberOfChecked(editable) !== 0
                        }
                        disabled={editable.length === 0}
                        inputProps={{
                          "aria-label": "all items selected",
                        }}
                      />
                    }
                    titleTypographyProps={{ variant: "subtitle1" }}
                    title={
                      <b
                        sx={{
                          fontFamily: "Urbanist, sans-serif",
                        }}
                      >{`${t("Roster")} : ${rosterId}`}</b>
                    }
                    subheader={`${numberOfChecked(editable)}/${
                      editable.length
                    } selected`}
                  />
                  <Divider />
                  <List
                    sx={{
                      width: 300,
                      maxHeight: "510px",
                      bgcolor: "background.paper",
                      color: "#2B3A42",
                    }}
                    dense
                    component="div"
                    role="list"
                  >
                    {editable.map((value) => {
                      const labelId = `transfer-list-all-item-${value}-label`;

                      return (
                        <ListItem
                          key={value.id}
                          role="listitem"
                          button
                          onClick={handleToggle(value)}
                          sx={{
                            backgroundColor: "#FFCE00",
                            color: "black",
                          }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={checked.indexOf(value) !== -1}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={labelId}
                            sx={{
                              fontFamily: "Urbanist, sans-serif",
                              color: "#2B3A42",
                            }}
                            primary={getTraineeName(value)}
                          />
                        </ListItem>
                      );
                    })}

                    {nonEditable.map((value) => {
                      const labelId = `transfer-list-all-item-${value}-label`;

                      return (
                        <ListItem key={value?.id} role="listitem">
                          <ListItemIcon></ListItemIcon>
                          <ListItemText
                            id={labelId}
                            sx={{
                              fontFamily: "Urbanist",
                            }}
                            primary={getTraineeName(value)}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ThemeProvider>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </Paper>
  );
};

CustomList.propTypes = {
  items: PropTypes.array,
  rosterId: PropTypes.any,
  isRosterLocked: PropTypes.any,
  canRosterCreateIdmTicket: PropTypes.any,
  techEquipmentSolutionId: PropTypes.number,
};

export default React.memo(CustomList);
